import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import {
  Loader2, Check, GraduationCap, ShieldCheck, Sparkles, AlertTriangle,
} from 'lucide-react';

const PACKAGES = [
  {
    id: 'monthly',
    label: 'Monthly',
    price: 9.99,
    period: '/month',
    perks: ['Everything in the free trial', '30-day access', 'Cancel anytime'],
    accent: 'sky',
  },
  {
    id: 'annual',
    label: 'Annual',
    price: 89,
    period: '/year',
    perks: ['Everything in the free trial', '365-day access', 'Save ~26% vs monthly'],
    accent: 'lime',
    badge: 'Best Value',
  },
];

export default function SubscribePage() {
  const { user, loading: authLoading } = useAuth();
  const [params] = useSearchParams();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const canceled = params.get('canceled') === '1';

  const sub = user
    ? {
        status: user.subscription_status,
        days_left: user.subscription_days_left,
        has_access: user.subscription_has_access,
      }
    : null;

  const startCheckout = async (packageId) => {
    setError('');
    setBusy(packageId);
    try {
      const res = await api.post('/subscription/checkout', {
        package: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not start checkout');
      setBusy(null);
    }
  };

  if (authLoading) {
    return <div className="max-w-6xl mx-auto px-6 py-24 flex justify-center"><Loader2 size={32} className="animate-spin text-amber-500" /></div>;
  }

  return (
    <div className="max-w-5xl mx-auto px-6 py-12">
      <div className="text-center mb-10 fade-up">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-lime-400 mb-2 flex items-center justify-center gap-2">
          <GraduationCap size={12} /> Academy
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-zinc-100 tracking-tight leading-none">
          UNLOCK THE <span className="text-gradient-hot">THEORY</span>
        </h1>
        <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
          On every solution, get the "Why This Works" section — trade theory, physics behind the fix, and real U.S. code references (NEC, IPC, IRC). Learn as you fix.
        </p>
      </div>

      {sub?.status === 'active' && (
        <div className="industrial-card border-t-4 border-t-lime-400 mb-8 flex items-center gap-3 !p-4 fade-up">
          <ShieldCheck size={24} className="text-lime-400" />
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-lime-400">Pro Member</div>
            <div className="text-zinc-100 font-semibold">
              You're subscribed · {sub.days_left} day{sub.days_left === 1 ? '' : 's'} of access remaining
            </div>
          </div>
        </div>
      )}
      {sub?.status === 'trial' && (
        <div className="industrial-card border-l-4 border-l-amber-400 mb-8 flex items-center gap-3 !p-4 bg-gradient-to-r from-amber-500/5 to-transparent fade-up">
          <Sparkles size={24} className="text-amber-400 pulse-gentle" />
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-amber-400">Free Trial</div>
            <div className="text-zinc-100">
              You have <span className="font-bold">{sub.days_left}</span> day{sub.days_left === 1 ? '' : 's'} left. Subscribe now to keep the Academy after your trial.
            </div>
          </div>
        </div>
      )}
      {canceled && (
        <div className="border border-zinc-800 bg-zinc-900 mb-6 px-4 py-3 text-zinc-400 text-sm font-mono">
          Checkout canceled. No charge was made.
        </div>
      )}
      {error && (
        <div className="border border-red-500/40 bg-red-500/10 text-red-400 px-4 py-3 mb-6 text-sm font-mono flex items-center gap-2">
          <AlertTriangle size={16} /> {error}
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-6">
        {PACKAGES.map((p) => (
          <div
            key={p.id}
            data-testid={`plan-${p.id}`}
            className={`industrial-card !p-6 border-t-4 ${
              p.accent === 'lime' ? 'border-t-lime-400 glow-lime' : 'border-t-sky-400 glow-sky'
            } relative`}
          >
            {p.badge && (
              <div className="absolute -top-3 right-4 bg-lime-400 text-zinc-950 font-mono text-[10px] uppercase tracking-widest px-2 py-1">
                {p.badge}
              </div>
            )}
            <div className="font-mono text-xs uppercase tracking-widest text-zinc-500">
              {p.label}
            </div>
            <div className="mt-2 flex items-baseline gap-1">
              <span className="font-display text-6xl text-zinc-100 tracking-tight">${p.price}</span>
              <span className="text-zinc-400 font-mono">{p.period}</span>
            </div>
            <ul className="mt-6 space-y-2">
              {p.perks.map((perk) => (
                <li key={perk} className="flex items-start gap-2 text-zinc-300 text-sm">
                  <Check size={16} className={p.accent === 'lime' ? 'text-lime-400 mt-0.5 shrink-0' : 'text-sky-400 mt-0.5 shrink-0'} />
                  <span>{perk}</span>
                </li>
              ))}
            </ul>
            <button
              data-testid={`subscribe-${p.id}`}
              onClick={() => startCheckout(p.id)}
              disabled={busy === p.id}
              className={p.accent === 'lime' ? 'btn-lime w-full mt-6 hover-wiggle' : 'btn-primary w-full mt-6 hover-wiggle'}
            >
              {busy === p.id ? <Loader2 size={16} className="animate-spin" /> : `Subscribe — $${p.price}`}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-8 text-center text-zinc-500 text-xs font-mono">
        Secure checkout by Stripe · Cancel anytime · <Link to="/app" className="text-amber-400 hover:underline">Back to app</Link>
      </div>
    </div>
  );
}
